import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Calendar, Clock, Phone, Mail, MapPin, Users, Loader2, RefreshCw, Home, LogOut, TrendingUp, Quote, FileText, ClipboardList, Receipt, CalendarDays, Briefcase } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import AdminLogin from './AdminLogin';
import PipelineView from './PipelineView';
import QuoteManagement from './QuoteManagement';
import CustomerManagement from './CustomerManagement';
import SurveyManagement from './SurveyManagement';
import InvoiceManagement from './InvoiceManagement/InvoiceManagement';
import AppointmentCalendar from './AppointmentCalendar';
import OrderPipeline from './OrderPipeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Setup axios interceptor to include auth token
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [authenticated]);

  // Check authentication on component mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Load requests when authenticated and filter changes
  useEffect(() => {
    if (authenticated) {
      loadHomeVisitRequests();
    }
  }, [statusFilter, authenticated]);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('admin_token');
    const loginTime = localStorage.getItem('admin_login_time');
    
    if (token && loginTime) {
      const now = Date.now();
      const loginTimestamp = parseInt(loginTime);
      const sessionDuration = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
      
      if (now - loginTimestamp < sessionDuration) {
        // Verify token with server
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          await axios.get(`${API}/admin/verify`);
          setAuthenticated(true);
        } catch (error) {
          // Token invalid, clear and logout
          handleLogout();
        }
      } else {
        // Session expired, clear localStorage
        handleLogout();
      }
    }
  };

  const handleLogin = (success, token) => {
    if (success && token) {
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_login_time', Date.now().toString());
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAuthenticated(true);
      toast({
        title: "Access Granted",
        description: "Welcome to the admin panel",
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_authenticated');
    localStorage.removeItem('admin_login_time');
    delete axios.defaults.headers.common['Authorization'];
    setAuthenticated(false);
    setRequests([]);
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully",
    });
  };

  const loadHomeVisitRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/calculator/home-visit/requests?status=${statusFilter}&limit=100`);
      setRequests(response.data.data.requests);
    } catch (error) {
      console.error('Error loading home visit requests:', error);
      toast({
        title: "Error",
        description: "Failed to load home visit requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (visitId, newStatus) => {
    setUpdatingStatus(visitId);
    try {
      await axios.patch(`${API}/calculator/home-visit/requests/${visitId}/status?status=${newStatus}`);
      
      // Update local state
      setRequests(prev => prev.map(req => 
        req.id === visitId ? { ...req, status: newStatus } : req
      ));
      
      toast({
        title: "Status Updated",
        description: `Request status changed to ${newStatus}`,
      });
      
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update request status",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPreferredDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const createQuoteFromHomeVisit = async (request) => {
    try {
      // Prepare quote data from home visit request
      const quoteData = {
        user_email: request.customer_email,
        customer_name: request.customer_name,
        customer_postcode: request.customer_postcode || '',
        windows: [{
          id: 1,
          label: 'Window 1',
          width: 1500,
          height: 1200,
          unit: 'mm',
          type: 'standard',
          area: 1.8,
          cost: 468,
          price_per_sqm: 260,
          shutter_colour: 'White',
          louvre_size: '63mm',
          control_type: 'Central Tilt Rod',
          hinge_colour: 'White',
          discount_percentage: 0
        }],
        overall_discount_percentage: 0,
        notes: `Quote created from home visit request. Original estimate: £${request.total_estimate}. Preferred visit: ${formatPreferredDate(request.preferred_date)}`,
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData)
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Quote Created Successfully",
          description: `Quote ${data.data.quote_number} created for ${request.customer_name}. Ready to customize and send.`
        });
        
        // Update the home visit request status to confirmed
        await updateRequestStatus(request.id, 'confirmed');
      } else {
        throw new Error(data.data?.error || 'Failed to create quote');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Quote Creation Failed",
        description: `Failed to create quote: ${error.message}`
      });
    }
  };

  // Calculate key metrics
  const getMetrics = () => {
    const totalRequests = requests.length;
    
    // Count by status
    const statusCounts = requests.reduce((acc, req) => {
      acc[req.status] = (acc[req.status] || 0) + 1;
      return acc;
    }, {});

    // Calculate total estimates
    const totalEstimateValue = requests.reduce((sum, req) => sum + (req.total_estimate || 0), 0);
    const averageEstimate = totalRequests > 0 ? totalEstimateValue / totalRequests : 0;

    // Calculate estimates by status
    const estimatesByStatus = requests.reduce((acc, req) => {
      if (!acc[req.status]) {
        acc[req.status] = { count: 0, total: 0 };
      }
      acc[req.status].count += 1;
      acc[req.status].total += req.total_estimate || 0;
      return acc;
    }, {});

    return {
      totalRequests,
      statusCounts,
      totalEstimateValue,
      averageEstimate,
      estimatesByStatus
    };
  };

  const metrics = getMetrics();

  // Show login form if not authenticated
  if (!authenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-white rounded-full shadow-lg">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900">Admin Panel</h1>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="absolute top-4 right-4 flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
          <p className="text-lg text-gray-600">
            Manage estimates pipeline and home visit requests
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-8">
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Estimates
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="quotes" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Quotes
            </TabsTrigger>
            <TabsTrigger value="surveys" className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Surveys
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex items-center gap-2">
              <Home className="h-4 w-4" />
              Home Visits
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="invoices-tab">
              <Receipt className="h-4 w-4" />
              Invoices
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="orders">
            <OrderPipeline />
          </TabsContent>
          
          <TabsContent value="pipeline">
            <PipelineView />
          </TabsContent>
          
          <TabsContent value="calendar">
            <AppointmentCalendar />
          </TabsContent>
          
          <TabsContent value="quotes">
            <QuoteManagement />
          </TabsContent>
          
          <TabsContent value="surveys">
            <SurveyManagement />
          </TabsContent>
          
          <TabsContent value="customers">
            <CustomerManagement />
          </TabsContent>
          
          <TabsContent value="visits">
            <HomeVisitRequestsView 
              requests={requests}
              metrics={metrics}
              loading={loading}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              updatingStatus={updatingStatus}
              loadHomeVisitRequests={loadHomeVisitRequests}
              updateRequestStatus={updateRequestStatus}
              createQuoteFromHomeVisit={createQuoteFromHomeVisit}
              getStatusBadgeColor={getStatusBadgeColor}
              formatDate={formatDate}
              formatPreferredDate={formatPreferredDate}
            />
          </TabsContent>
          
          <TabsContent value="invoices">
            <InvoiceManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const HomeVisitRequestsView = ({ 
  requests, 
  metrics, 
  loading, 
  statusFilter, 
  setStatusFilter, 
  updatingStatus,
  loadHomeVisitRequests,
  updateRequestStatus,
  createQuoteFromHomeVisit,
  getStatusBadgeColor,
  formatDate,
  formatPreferredDate
}) => {
  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Requests */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-3xl font-bold text-gray-900">{metrics.totalRequests}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Pending: {metrics.statusCounts.pending || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Confirmed: {metrics.statusCounts.confirmed || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Estimate Value */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Estimates</p>
                <p className="text-3xl font-bold text-green-600">£{metrics.totalEstimateValue.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <span className="text-xl font-bold text-green-600">£</span>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Avg: £{metrics.averageEstimate.toFixed(2)} per request
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Pending Requests */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                <p className="text-3xl font-bold text-yellow-600">{metrics.statusCounts.pending || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Value: £{(metrics.estimatesByStatus.pending?.total || 0).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Completed Requests */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-3xl font-bold text-green-600">{metrics.statusCounts.completed || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Home className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Value: £{(metrics.estimatesByStatus.completed?.total || 0).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Filter by status:</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-gray-600">
            {requests.length} request{requests.length !== 1 ? 's' : ''}
          </div>
        </div>
        
        <Button 
          onClick={loadHomeVisitRequests} 
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-lg text-gray-600">Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <Home className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-600">No home visit requests found</p>
            <p className="text-sm text-gray-500 mt-2">
              {statusFilter !== 'all' ? `No requests with status "${statusFilter}"` : 'Requests will appear here when customers submit them'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {requests.map((request) => (
            <Card key={request.id} className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl text-gray-900 mb-2">
                      {request.customer_name}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusBadgeColor(request.status)}>
                        {request.status.toUpperCase()}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        Submitted: {formatDate(request.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">
                      £{request.total_estimate?.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {request.windows_count} window{request.windows_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Customer Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{request.customer_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{request.phone_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{request.customer_postcode}</span>
                  </div>
                </div>

                <Separator />

                {/* Visit Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">Preferred Date:</span>
                    <span className="text-sm">{formatPreferredDate(request.preferred_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">Preferred Time:</span>
                    <span className="text-sm">{request.preferred_time}</span>
                  </div>
                </div>

                {/* Additional Notes */}
                {request.additional_notes && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Additional Notes:</h4>
                    <p className="text-sm text-gray-600">{request.additional_notes}</p>
                  </div>
                )}

                {/* Status Update */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Update Status:</span>
                    <div className="flex gap-2">
                      {['pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
                        <Button
                          key={status}
                          size="sm"
                          variant={request.status === status ? "default" : "outline"}
                          onClick={() => updateRequestStatus(request.id, status)}
                          disabled={updatingStatus === request.id || request.status === status}
                          className="text-xs"
                        >
                          {updatingStatus === request.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            status
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Create Quote Button */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => createQuoteFromHomeVisit(request)}
                      style={{backgroundColor: '#8CB89F'}}
                      className="hover:opacity-90 text-white text-xs flex items-center gap-1"
                    >
                      <FileText className="h-3 w-3" />
                      Create Quote
                    </Button>
                    <div className="text-xs text-gray-500">
                      ID: {request.id.slice(0, 8)}...
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;